import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Server, Copy, CheckCircle2, Circle, ExternalLink } from "lucide-react";
import { toast } from "sonner";

function Row({ label, value, mono = true, copyable = false }) {
  const copy = () => {
    navigator.clipboard.writeText(value || "");
    toast.success("Copied");
  };
  return (
    <div className="grid grid-cols-3 gap-4 py-2 border-b border-zinc-100 last:border-none">
      <div className="text-xs text-zinc-500 uppercase tracking-widest">{label}</div>
      <div className={`col-span-2 text-sm ${mono ? "font-mono" : ""} text-zinc-900 flex items-center justify-between gap-2 min-w-0`}>
        <span className="truncate">{value || "—"}</span>
        {copyable && value && (
          <button
            onClick={copy}
            className="opacity-60 hover:opacity-100 shrink-0"
            data-testid={`copy-${label.toLowerCase().replace(/\s/g, "-")}`}
            aria-label="copy"
          ><Copy className="w-3.5 h-3.5" /></button>
        )}
      </div>
    </div>
  );
}

export default function ProductionSettings() {
  const { data } = useQuery({
    queryKey: ["production-settings"],
    queryFn: async () => (await api.get("/platform/production-settings")).data,
  });

  if (!data) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="p-8 max-w-5xl space-y-8" data-testid="production-settings-page">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-md border border-zinc-200 grid place-items-center bg-white">
          <Server className="w-4 h-4 text-zinc-700" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Production Settings</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Runtime configuration surface. Values are read-only — set them via environment variables on deploy.</p>
        </div>
      </div>

      <section className="border border-zinc-200 rounded-lg bg-white">
        <div className="px-5 py-3 border-b border-zinc-100 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Application</div>
        <div className="px-5 py-2">
          <Row label="Name" value={data.app.name} mono={false} />
          <Row label="Subtitle" value={data.app.subtitle} mono={false} />
          <Row label="Version" value={data.app.version} />
        </div>
      </section>

      <section className="border border-zinc-200 rounded-lg bg-white">
        <div className="px-5 py-3 border-b border-zinc-100 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Domain</div>
        <div className="px-5 py-2">
          <Row label="Frontend URL" value={data.domain.frontend_url} copyable />
          <Row label="Host" value={data.domain.host} copyable />
          <Row label="CORS Origins" value={data.domain.cors_origins} />
        </div>
      </section>

      <section className="border border-zinc-200 rounded-lg bg-white">
        <div className="px-5 py-3 border-b border-zinc-100 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Cookies</div>
        <div className="px-5 py-2">
          <Row label="Secure" value={data.cookies.secure} />
          <Row label="SameSite" value={data.cookies.samesite} />
          <Row label="Domain" value={data.cookies.domain} />
        </div>
      </section>

      <section className="border border-zinc-200 rounded-lg bg-white">
        <div className="px-5 py-3 border-b border-zinc-100 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">DNS Instructions</div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-zinc-700">Point your custom subdomain to the Emergent platform via a <span className="font-mono">{data.dns_instructions.record_type}</span> record at your registrar (Cloudflare / GoDaddy):</p>
          <div className="rounded-md border border-zinc-200 bg-zinc-50 p-4 font-mono text-xs">
            <div className="grid grid-cols-4 gap-2">
              <div className="text-zinc-500 uppercase tracking-widest text-[10px]">Type</div>
              <div className="text-zinc-500 uppercase tracking-widest text-[10px]">Name</div>
              <div className="text-zinc-500 uppercase tracking-widest text-[10px]">Value</div>
              <div className="text-zinc-500 uppercase tracking-widest text-[10px]">TTL</div>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-2">
              <div>{data.dns_instructions.record_type}</div>
              <div>{data.dns_instructions.name}</div>
              <div className="truncate flex items-center gap-2">
                {data.dns_instructions.value}
                <button
                  className="opacity-60 hover:opacity-100"
                  onClick={() => { navigator.clipboard.writeText(data.dns_instructions.value); toast.success("Copied"); }}
                  data-testid="copy-cname-value"
                ><Copy className="w-3 h-3" /></button>
              </div>
              <div>{data.dns_instructions.ttl}</div>
            </div>
          </div>
          <p className="text-xs text-zinc-600 leading-relaxed">{data.dns_instructions.notes}</p>
          <a
            href="https://docs.emergent.sh/custom-domain"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-900 underline underline-offset-2"
          >Emergent custom domain guide <ExternalLink className="w-3 h-3" /></a>
        </div>
      </section>

      <section className="border border-zinc-200 rounded-lg bg-white">
        <div className="px-5 py-3 border-b border-zinc-100 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Go-Live Checklist</div>
        <div className="px-5 py-4 space-y-2">
          {data.checklist.map((c) => (
            <div key={c.key} className="flex items-center gap-3 text-sm" data-testid={`check-${c.key}`}>
              {c.done
                ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                : <Circle className="w-4 h-4 text-zinc-300 shrink-0" />}
              <span className={c.done ? "text-zinc-500 line-through" : "text-zinc-800"}>{c.label}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
